import React, { Component } from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import BuildMinMaxBy from '../RangeSettings/BuildMinMaxBy/BuildMinMaxBy';
import MidPointSetting from '../RangeSettings/MidPointSetting/MidPointSetting';
import RangeTypeSetting from '../RangeSettings/RangeTypeSetting';
import RoundingSetting from '../RangeSettings/RoundingSetting';
import {
  adjustCurrentMidpointClick,
  handleMidpointAdjustmentValueUpdate,
  maxPercentChange,
  minPercentChange,
  proposedMaxSelect,
  proposedMinSelect,
  proposedRangeSelect,
  rangeSpreadChange,
  rangeTypeClick,
  roundToChange,
  saveBuildMinMaxBy
} from '../actions/expandableHeader';
import { BUILD_BY_TARGET, MARKET_RANGE_TYPE, MIDPOINT_RANGE_TYPE } from '../Utils/constants';
import { isLab } from '../../../redux/stateSelectors';
import './ExpandableHeader.scss';

export class ExpandableHeader extends Component {
  static propTypes = {
    adjustCurrentMidpointClick: func,
    buildMinMaxBy: string,
    handleMidpointAdjustmentValueUpdate: func,
    isExpandable: bool,
    isExpanded: bool,
    isLab: bool,
    maxPercentChange: func,
    maxPercentageOfMid: number,
    midpointAdjustment: number,
    minPercentChange: func,
    minPercentageOfMid: number,
    proposedMaxSelect: func,
    proposedMinSelect: func,
    proposedRangeSelect: func,
    proposedRangeSetting: string,
    rangeSpreadChange: func,
    rangeType: string,
    rangeTypeClick: func,
    roundOptions: arrayOf(
      shape({
        name: string,
        value: number
      })
    ),
    roundTo: number,
    roundToChange: func,
    saveBuildMinMaxBy: func,
    setProposedRangeOptions: arrayOf(
      shape({
        name: string,
        value: string
      })
    ),
    targetRangeSpread: number,
    toggle: func
  };

  static defaultProps = {
    isExpandable: true
  };

  getSummaryText() {
    const {
      buildMinMaxBy,
      maxPercentageOfMid,
      midpointAdjustment,
      minPercentageOfMid,
      proposedRangeSetting,
      rangeType,
      setProposedRangeOptions,
      targetRangeSpread
    } = this.props;
    const formattedTargetRangeSpread = Math.round(targetRangeSpread * 100);
    let summaryText = 'Range Has Not Been Set';

    if (proposedRangeSetting || rangeType === MIDPOINT_RANGE_TYPE) {
      if (rangeType === MARKET_RANGE_TYPE) {
        const selectedRangeOption = setProposedRangeOptions.find(opt => opt.value === proposedRangeSetting);

        if (buildMinMaxBy === BUILD_BY_TARGET) {
          summaryText = `${selectedRangeOption?.name}, Range Spread of ${formattedTargetRangeSpread}%`;
        } else {
          const minPercent = Math.round(minPercentageOfMid * 100);
          const maxPercent = Math.round(maxPercentageOfMid * 100);

          summaryText = `${selectedRangeOption?.name}, Min ${minPercent}% of Mid, Max ${maxPercent}% of Mid`;
        }
      } else {
        const midPercent = Math.round(midpointAdjustment * 100);

        if (buildMinMaxBy === BUILD_BY_TARGET) {
          summaryText = `Mid ${midPercent}%, Range Spread of ${formattedTargetRangeSpread}%`;
        } else {
          const minPercent = Math.round(minPercentageOfMid * 100);
          const maxPercent = Math.round(maxPercentageOfMid * 100);

          summaryText = `Mid ${midPercent}%, Min is ${minPercent}% of Mid, Max is ${maxPercent}% of Mid`;
        }
      }
    }

    return summaryText;
  }

  render() {
    const {
      adjustCurrentMidpointClick,
      buildMinMaxBy,
      handleMidpointAdjustmentValueUpdate,
      isExpandable,
      isExpanded,
      isLab,
      maxPercentChange,
      maxPercentageMarketElement,
      maxPercentageOfMid,
      midpointAdjustment,
      minPercentChange,
      minPercentageMarketElement,
      minPercentageOfMid,
      proposedMaxSelect,
      proposedMinSelect,
      proposedRangeSelect,
      proposedRangeSetting,
      rangeSpreadChange,
      rangeType,
      rangeTypeClick,
      roundOptions,
      roundTo,
      roundToChange,
      saveBuildMinMaxBy,
      setProposedRangeOptions,
      targetRangeSpread,
      toggle
    } = this.props;
    const headerClass = isLab ? 'lab-jobRanges__expandableHeader' : 'mp-jobRanges__expandableHeader';

    return (
      <div className={classnames('jbr__expandable-header', headerClass)} data-test-id="jbr-model-settings">
        <div
          className={classnames('top-bar', { expandable: isExpandable })}
          data-test-id="jbr-model-settings-expand-bar"
          onClick={toggle}
        >
          <span className="span--top-bar">
            Current Target for Jobs: <b className="summary__text-bold">{this.getSummaryText()}</b>
          </span>
          {isExpandable && <i className={isExpanded ? 'icon-up-open' : 'icon-down-open'} />}
        </div>
        <div className="stat__group-row">
          <RangeTypeSetting
            className="model__range-type-container"
            handleRangeTypeOptionClick={rangeTypeClick}
            optionsClassName="range-type-options"
            rangeType={rangeType}
            rangeTypeOptions={['Market', 'Midpoint']}
          />
          <MidPointSetting
            adjustCurrentMidpointClick={adjustCurrentMidpointClick}
            handleMidpointAdjustmentValueUpdate={handleMidpointAdjustmentValueUpdate}
            midpointAdjustment={midpointAdjustment}
            proposedRangeSelect={proposedRangeSelect}
            proposedRangeSetting={proposedRangeSetting}
            rangeType={rangeType}
            setProposedRangeOptions={setProposedRangeOptions}
          />
          <BuildMinMaxBy
            buildMinMaxBy={buildMinMaxBy}
            handleMaxPercentChange={maxPercentChange}
            handleMinPercentChange={minPercentChange}
            handleRangeSpreadChange={rangeSpreadChange}
            isGlobal={true}
            maxPercentageMarketElement={maxPercentageMarketElement}
            maxPercentageOfMid={maxPercentageOfMid}
            minPercentageMarketElement={minPercentageMarketElement}
            minPercentageOfMid={minPercentageOfMid}
            proposedMaxSelect={proposedMaxSelect}
            proposedMinSelect={proposedMinSelect}
            rangeType={rangeType}
            saveBuildMinMaxBy={saveBuildMinMaxBy}
            setProposedRangeOptions={setProposedRangeOptions}
            targetRangeSpread={targetRangeSpread}
          />
          <RoundingSetting
            className="stat__group-content round-to-nearest-content"
            handleRoundToSelect={roundToChange}
            roundOptions={roundOptions}
            roundTo={roundTo}
          />
        </div>
      </div>
    );
  }
}

const connectState = state => ({
  buildMinMaxBy: state.jobRanges.modelDetails.jobRangeModel.buildMinMaxBy,
  isLab: isLab(state),
  maxPercentageMarketElement: state.jobRanges.modelDetails.jobRangeModel.maxPercentageMarketElement,
  maxPercentageOfMid: state.jobRanges.modelDetails.jobRangeModel.maxPercentageOfMid,
  midpointAdjustment: state.jobRanges.modelDetails.jobRangeModel.midpointAdjustment,
  minPercentageMarketElement: state.jobRanges.modelDetails.jobRangeModel.minPercentageMarketElement,
  minPercentageOfMid: state.jobRanges.modelDetails.jobRangeModel.minPercentageOfMid,
  proposedRangeSetting: state.jobRanges.modelDetails.jobRangeModel.proposedRangeSetting,
  rangeType: state.jobRanges.modelDetails.jobRangeModel.rangeType,
  roundTo: state.jobRanges.modelDetails.jobRangeModel.roundTo,
  roundOptions: state.jobRanges.modelDetails.roundOptions,
  setProposedRangeOptions: state.jobRanges.modelDetails.setProposedRangeOptions,
  targetRangeSpread: state.jobRanges.modelDetails.jobRangeModel.targetRangeSpread
});

const actions = {
  adjustCurrentMidpointClick,
  handleMidpointAdjustmentValueUpdate,
  maxPercentChange,
  minPercentChange,
  proposedMaxSelect,
  proposedMinSelect,
  proposedRangeSelect,
  rangeSpreadChange,
  rangeTypeClick,
  roundToChange,
  saveBuildMinMaxBy
};

export default connect(connectState, actions)(ExpandableHeader);
